/**
 * API Configuration
 * 
 * Configuration is loaded in the following priority order:
 * 1. Tenant URL parameter (?t=tenantId) - for multi-tenant iframe embeds
 * 2. Server-side config.json file - for self-hosted instances
 * 3. Browser localStorage - for standalone usage with setup page
 * 4. Development defaults - for local development
 * 
 * MULTI-TENANT IFRAME SUPPORT:
 * - Customer embeds: <iframe src="https://classifieds.fakebrains.com/create-ad?t=yourpaper">
 * - Server returns tenant config with CSP frame-ancestors enforcement
 * - End-users never see the setup page
 * 
 * SELF-HOSTED SUPPORT:
 * - Customer deploys app with their own config.json file
 * 
 * STANDALONE USAGE:
 * - Users visit the setup page and configure their API credentials
 * - Settings are stored in browser localStorage
 */

// ============================================================================
// TYPES
// ============================================================================

export interface BrowseGroupOverride {
  groupClassificationKeys: number[];
  daysBeforeToday: number;
  daysAfterToday: number;
}

interface ServerConfig {
  tenantId?: string;
  apiBaseUrl: string;
  apiKey: string;
  theme?: string;
  showHeader?: boolean;
  requireSiteId?: boolean;
  allowOrderCreation?: boolean;
  // Site-specific fields
  siteId?: string | null;
  siteName?: string | null;
  allowedPublicationKeys?: number[] | null;
  presentAllPublications?: boolean;
  allowedGroupClassificationKeys?: number[] | null;
  majorCategoryOverviewEnabled?: boolean;
  // Browse date range config
  browseDaysBeforeToday?: number | null;
  browseDaysAfterToday?: number | null;
  browsePageSize?: number | null;
  browsePageThreshold?: number | null;
  browseGroupOverrides?: BrowseGroupOverride[] | null;
  // Customer-facing listing labels
  browseLabel?: string | null;
  listingLabelSingular?: string | null;
  listingLabelPlural?: string | null;
  // Customer-facing theme color overrides
  buttonBackgroundColor?: string | null;
  buttonTextColor?: string | null;
  accentLineColor?: string | null;
  pageBackgroundColor?: string | null;
}

interface TenantConfig {
  tenantId: string;
  apiBaseUrl: string;
  apiKey: string;
  theme?: string;
  showHeader?: boolean;
  requireSiteId?: boolean;
  allowOrderCreation?: boolean;
  // Site-specific fields from server
  siteId?: string | null;
  siteName?: string | null;
  allowedPublicationKeys?: number[] | null;
  presentAllPublications?: boolean;
  allowedGroupClassificationKeys?: number[] | null;
  majorCategoryOverviewEnabled?: boolean;
  // Browse date range config
  browseDaysBeforeToday?: number | null;
  browseDaysAfterToday?: number | null;
  browsePageSize?: number | null;
  browsePageThreshold?: number | null;
  browseGroupOverrides?: BrowseGroupOverride[] | null;
  // Customer-facing listing labels
  browseLabel?: string | null;
  listingLabelSingular?: string | null;
  listingLabelPlural?: string | null;
  // Customer-facing theme color overrides
  buttonBackgroundColor?: string | null;
  buttonTextColor?: string | null;
  accentLineColor?: string | null;
  pageBackgroundColor?: string | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Hardcoded API base URL (same for all tenants)
 */
const DEFAULT_API_BASE_URL = 'https://accountscoutapi.fakebrains.com';
const DEFAULT_BROWSE_LABEL = 'Classified Ads';
const DEFAULT_LISTING_LABEL_SINGULAR = 'Classified Ad';
const DEFAULT_LISTING_LABEL_PLURAL = 'Classified Ads';
const DEFAULT_BUTTON_TEXT_COLOR = '#ffffff';

// ============================================================================
// STATE - Cached config (loaded once on app startup)
// ============================================================================

let serverConfig: ServerConfig | null = null;
let serverConfigLoaded = false;
let serverConfigLoadPromise: Promise<void> | null = null;
let currentTenantId: string | null = null;
let currentSiteId: string | null = null;

// ============================================================================
// LOCALSTORAGE KEYS
// ============================================================================
const STORAGE_KEY_API_URL = 'coe_api_base_url';
const STORAGE_KEY_API_KEY = 'coe_api_key';

// ============================================================================
// DEVELOPMENT DEFAULTS
// ============================================================================

/**
 * Toggle between local and production API for development
 */
const USE_LOCAL_API = false;

/**
 * Are we running in development mode (Vite dev server)?
 */
const IS_DEV_MODE = import.meta.env.DEV;

/**
 * Local API URL (IIS Express on localhost)
 */
const LOCAL_API_BASE_URL = 'https://localhost:44363';

/**
 * Default API Key for development
 */
const DEFAULT_DEV_API_KEY = '3dc5e12f-b45c-4b02-a039-5bada77a71f9'; // prDemo

// ============================================================================
// TENANT AND SITE URL PARAMETERS
// ============================================================================

/**
 * Get tenant ID from URL query parameter
 */
export function getTenantIdFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('t');
}

/**
 * Get site ID from URL query parameter
 */
export function getSiteIdFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('s');
}

/**
 * Get the current tenant ID (if any)
 */
export function getCurrentTenantId(): string | null {
  return currentTenantId;
}

/**
 * Get the current site ID (if any)
 */
export function getCurrentSiteId(): string | null {
  return currentSiteId;
}

// ============================================================================
// CONFIG LOADING
// ============================================================================

/**
 * Load configuration based on priority:
 * 1. Tenant URL parameter (?t=xxx) with optional site (?s=xxx)
 * 2. config.json file
 * 3. localStorage
 */
export async function loadServerConfig(): Promise<void> {
  if (serverConfigLoaded) {
    return;
  }
  
  if (serverConfigLoadPromise) {
    return serverConfigLoadPromise;
  }
  
  serverConfigLoadPromise = (async () => {
    // 1. Check for tenant URL parameter (with optional site)
    const tenantId = getTenantIdFromUrl();
    const siteId = getSiteIdFromUrl();
    
    if (tenantId) {
      currentTenantId = tenantId;
      currentSiteId = siteId;
      
      try {
        // Build URL with tenant and optional site parameters
        let configUrl = `/api/tenant-config?t=${encodeURIComponent(tenantId)}`;
        if (siteId) {
          configUrl += `&s=${encodeURIComponent(siteId)}`;
        }
        
        const response = await fetch(configUrl, {
          cache: 'no-cache',
        });
        
        if (response.ok) {
          const config: TenantConfig = await response.json();
          
          serverConfig = {
            tenantId: config.tenantId,
            apiBaseUrl: config.apiBaseUrl || DEFAULT_API_BASE_URL,
            apiKey: config.apiKey,
            theme: config.theme || 'dark',
            showHeader: config.showHeader ?? false, // Default to hidden for iframe embeds
            requireSiteId: config.requireSiteId === true,
            allowOrderCreation: config.allowOrderCreation !== false,
            // Site-specific configuration
            siteId: config.siteId || null,
            siteName: config.siteName || null,
            allowedPublicationKeys: config.allowedPublicationKeys || null,
            presentAllPublications: config.presentAllPublications === true,
            allowedGroupClassificationKeys: config.allowedGroupClassificationKeys || null,
            majorCategoryOverviewEnabled: typeof config.majorCategoryOverviewEnabled === 'boolean'
              ? config.majorCategoryOverviewEnabled
              : true,
            // Browse date range config
            browseDaysBeforeToday: config.browseDaysBeforeToday ?? null,
            browseDaysAfterToday: config.browseDaysAfterToday ?? null,
            browsePageSize: config.browsePageSize ?? null,
            browsePageThreshold: config.browsePageThreshold ?? null,
            browseGroupOverrides: config.browseGroupOverrides ?? null,
            browseLabel: typeof config.browseLabel === 'string' ? config.browseLabel : null,
            listingLabelSingular: typeof config.listingLabelSingular === 'string' ? config.listingLabelSingular : null,
            listingLabelPlural: typeof config.listingLabelPlural === 'string' ? config.listingLabelPlural : null,
            buttonBackgroundColor: normalizeHexColor(config.buttonBackgroundColor),
            buttonTextColor: normalizeHexColor(config.buttonTextColor),
            accentLineColor: normalizeHexColor(config.accentLineColor),
            pageBackgroundColor: normalizeHexColor(config.pageBackgroundColor),
          };
          console.log(`Loaded tenant configuration for: ${tenantId}${siteId ? ` (site: ${siteId})` : ''} (theme: ${serverConfig.theme}, showHeader: ${serverConfig.showHeader}, publications: ${serverConfig.allowedPublicationKeys?.length ?? 'all'}, groups: ${serverConfig.allowedGroupClassificationKeys?.length ?? 'all'})`);
          serverConfigLoaded = true;
          return;
        } else {
          console.error(`Failed to load tenant config for: ${tenantId}${siteId ? ` (site: ${siteId})` : ''}`);
        }
      } catch (error) {
        console.error('Error loading tenant config:', error);
      }

      // A tenant URL is authoritative. Never fall back to config.json or local development
      // credentials when the tenant/site request is invalid (including a required missing site ID).
      serverConfig = null;
      serverConfigLoaded = true;
      return;
    }
    
    // 2. Try to fetch config.json from the server root
    try {
      const response = await fetch('/config.json', {
        cache: 'no-cache',
      });
      
      if (response.ok) {
        const config = await response.json();
        
        if (config.apiBaseUrl && config.apiKey) {
          serverConfig = {
            apiBaseUrl: config.apiBaseUrl,
            apiKey: config.apiKey,
          };
          console.log('Loaded configuration from config.json');
          serverConfigLoaded = true;
          return;
        }
      }
    } catch (error) {
      // config.json doesn't exist or is invalid - that's okay
      console.log('No config.json found, checking localStorage/setup page');
    }
    
    serverConfigLoaded = true;
  })();
  
  return serverConfigLoadPromise;
}

/**
 * Check if server config (tenant or config.json) is available
 */
export function hasServerConfig(): boolean {
  return serverConfig !== null;
}

/**
 * Check if we're in tenant mode (loaded from ?t= parameter)
 */
export function isTenantMode(): boolean {
  return currentTenantId !== null && serverConfig !== null;
}

/**
 * Check if we're running in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}

/**
 * Get the current theme (from tenant config or default)
 */
export function getTheme(): string {
  if (serverConfig && serverConfig.theme) {
    return serverConfig.theme;
  }
  return 'dark';  // Default theme
}

/**
 * Get whether to show the header (from tenant config)
 * Default is false (hidden) for iframe embeds, true for standalone
 */
export function getShowHeader(): boolean {
  if (serverConfig) {
    return serverConfig.showHeader ?? false; // Default hidden for tenants
  }
  return true; // Show header for standalone/localStorage config
}

/**
 * Get whether the current tenant/site experience permits customers to create orders.
 * This is a presentation policy only; it is not an AccountScout API authorization boundary.
 */
export function isOrderCreationAllowed(): boolean {
  return serverConfig?.allowOrderCreation !== false;
}

/**
 * Get whether the tenant requires every public URL to identify a configured site.
 */
export function isSiteIdRequired(): boolean {
  return serverConfig?.requireSiteId === true;
}

/**
 * Check if we're in site mode (loaded from ?t=xxx&s=xxx parameters)
 */
export function isSiteMode(): boolean {
  return currentSiteId !== null && serverConfig?.siteId !== null;
}

/**
 * Get the site name (if in site mode)
 */
export function getSiteName(): string | null {
  return serverConfig?.siteName ?? null;
}

/**
 * Get the raw allowed publication keys for the current site.
 * These keys continue to control Create Ad package availability even when the site presents
 * ads from all publications. Returns null if the site has no publication restriction.
 */
export function getAllowedPublicationKeys(): number[] | null {
  return serverConfig?.allowedPublicationKeys ?? null;
}

/**
 * Get whether browse presentment should ignore the site's allowed publication keys.
 * Missing configuration defaults to false so existing sites retain their current behavior.
 */
export function getPresentAllPublications(): boolean {
  return serverConfig?.presentAllPublications === true;
}

/**
 * Get the effective publication filter for browse presentment.
 * Null delegates publication selection to the backend's globally enabled online publications.
 */
export function getPresentmentPublicationKeys(): number[] | null {
  return getPresentAllPublications() ? null : getAllowedPublicationKeys();
}

/**
 * Get allowed group classification keys for the current site.
 * Returns null if all groups are allowed (no site or site has no restrictions).
 */
export function getAllowedGroupClassificationKeys(): number[] | null {
  return serverConfig?.allowedGroupClassificationKeys ?? null;
}

/**
 * Get whether selecting a major or intermediate category opens its combined listing overview.
 * Missing or invalid configuration defaults to enabled.
 */
export function getMajorCategoryOverviewEnabled(): boolean {
  return serverConfig?.majorCategoryOverviewEnabled ?? true;
}

/**
 * Get browse days before today (from tenant/site config).
 * Returns 6 if not configured.
 */
export function getBrowseDaysBeforeToday(): number {
  return serverConfig?.browseDaysBeforeToday ?? 6;
}

/**
 * Get browse days after today (from tenant/site config).
 * Returns 7 if not configured.
 */
export function getBrowseDaysAfterToday(): number {
  return serverConfig?.browseDaysAfterToday ?? 7;
}

/**
 * Get browse page size for pagination (from tenant/site config).
 * Returns 50 if not configured.
 */
export function getBrowsePageSize(): number {
  return serverConfig?.browsePageSize ?? 50;
}

/**
 * Get browse page threshold — classifications with more ads than this will paginate.
 * Returns 100 if not configured.
 */
export function getBrowsePageThreshold(): number {
  return serverConfig?.browsePageThreshold ?? 100;
}

/**
 * Get browse group classification overrides for custom date ranges.
 * Returns null if no overrides configured.
 */
export function getBrowseGroupOverrides(): BrowseGroupOverride[] | null {
  return serverConfig?.browseGroupOverrides ?? null;
}

function getConfiguredListingLabel(label: string | null | undefined): string | null {
  if (typeof label !== 'string') {
    return null;
  }
  const trimmed = label.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function normalizeHexColor(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  const match = trimmed.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) {
    return null;
  }

  const hex = match[1].toLowerCase();
  if (hex.length === 3) {
    return `#${hex.split('').map(ch => ch + ch).join('')}`;
  }

  return `#${hex}`;
}

/**
 * Get the browse page title. An empty string intentionally hides only the title.
 */
export function getBrowseLabel(): string {
  return typeof serverConfig?.browseLabel === 'string'
    ? serverConfig.browseLabel
    : DEFAULT_BROWSE_LABEL;
}

/**
 * Check whether listing labels were configured explicitly.
 */
export function hasCustomListingLabels(): boolean {
  return getConfiguredListingLabel(serverConfig?.listingLabelSingular) !== null ||
    getConfiguredListingLabel(serverConfig?.listingLabelPlural) !== null;
}

/**
 * Get the singular customer-facing listing label.
 */
export function getListingLabelSingular(): string {
  return getConfiguredListingLabel(serverConfig?.listingLabelSingular) ?? DEFAULT_LISTING_LABEL_SINGULAR;
}

/**
 * Get the plural customer-facing listing label.
 */
export function getListingLabelPlural(): string {
  return getConfiguredListingLabel(serverConfig?.listingLabelPlural) ?? DEFAULT_LISTING_LABEL_PLURAL;
}

/**
 * Get the customer-facing listing label for a count.
 */
export function getListingLabel(count: number): string {
  return count === 1 ? getListingLabelSingular() : getListingLabelPlural();
}

export interface ThemeColorOverrides {
  buttonBackgroundColor: string | null;
  buttonTextColor: string | null;
  accentLineColor: string | null;
  pageBackgroundColor: string | null;
}

/**
 * Get normalized customer-facing theme color overrides.
 */
export function getThemeColorOverrides(): ThemeColorOverrides {
  const buttonBackgroundColor = normalizeHexColor(serverConfig?.buttonBackgroundColor);
  const buttonTextColor = normalizeHexColor(serverConfig?.buttonTextColor);
  const accentLineColor = normalizeHexColor(serverConfig?.accentLineColor) ?? buttonBackgroundColor;
  const pageBackgroundColor = normalizeHexColor(serverConfig?.pageBackgroundColor);

  return {
    buttonBackgroundColor,
    buttonTextColor: buttonTextColor ?? (buttonBackgroundColor ? DEFAULT_BUTTON_TEXT_COLOR : null),
    accentLineColor,
    pageBackgroundColor,
  };
}

// ============================================================================
// CONFIGURATION FUNCTIONS
// ============================================================================

/**
 * Check if the API is configured (via tenant, config.json, localStorage, or dev defaults)
 */
export function isApiConfigured(): boolean {
  // Check server config first (tenant or config.json)
  if (serverConfig) {
    return true;
  }

  // A URL containing ?t= is an authoritative tenant request. If that lookup failed,
  // never substitute unrelated browser credentials or development defaults.
  if (currentTenantId) {
    return false;
  }
  
  // In dev mode with local API, always configured
  if (IS_DEV_MODE && USE_LOCAL_API) {
    return true;
  }
  
  // Check localStorage for configuration
  const savedUrl = localStorage.getItem(STORAGE_KEY_API_URL);
  const savedKey = localStorage.getItem(STORAGE_KEY_API_KEY);
  
  return !!(savedUrl && savedKey);
}

/**
 * Get the configured API base URL
 */
function getApiBaseUrl(): string {
  // Server config takes priority (tenant or config.json)
  if (serverConfig) {
    return serverConfig.apiBaseUrl;
  }

  if (currentTenantId) {
    return '';
  }
  
  // In dev mode, use Vite proxy (empty string) or localhost
  if (IS_DEV_MODE) {
    if (USE_LOCAL_API) {
      return ''; // Vite proxy handles /coe/* requests
    }
  }
  
  // Check localStorage
  const savedUrl = localStorage.getItem(STORAGE_KEY_API_URL);
  if (savedUrl) {
    return savedUrl;
  }
  
  // Fallback to local API for development
  if (USE_LOCAL_API) {
    return LOCAL_API_BASE_URL;
  }
  
  // Not configured
  return '';
}

/**
 * Get the configured API key
 */
function getApiKey(): string {
  // Server config takes priority (tenant or config.json)
  if (serverConfig) {
    return serverConfig.apiKey;
  }

  if (currentTenantId) {
    return '';
  }
  
  // Check localStorage first
  const savedKey = localStorage.getItem(STORAGE_KEY_API_KEY);
  if (savedKey) {
    return savedKey;
  }
  
  // Fallback to dev key if using local API
  if (USE_LOCAL_API) {
    return DEFAULT_DEV_API_KEY;
  }
  
  // Not configured
  return '';
}

/**
 * Save API configuration to localStorage
 */
export function saveApiConfig(baseUrl: string, apiKey: string): void {
  localStorage.setItem(STORAGE_KEY_API_URL, baseUrl);
  localStorage.setItem(STORAGE_KEY_API_KEY, apiKey);
}

/**
 * Clear API configuration from localStorage
 */
export function clearApiConfig(): void {
  localStorage.removeItem(STORAGE_KEY_API_URL);
  localStorage.removeItem(STORAGE_KEY_API_KEY);
}

/**
 * Get the current API configuration (for display in setup page)
 */
export function getStoredApiConfig(): { baseUrl: string; apiKey: string } | null {
  // If server config exists, return that
  if (serverConfig) {
    return {
      baseUrl: serverConfig.apiBaseUrl,
      apiKey: serverConfig.apiKey,
    };
  }

  if (currentTenantId) {
    return null;
  }
  
  const baseUrl = localStorage.getItem(STORAGE_KEY_API_URL);
  const apiKey = localStorage.getItem(STORAGE_KEY_API_KEY);
  
  if (baseUrl && apiKey) {
    return { baseUrl, apiKey };
  }
  
  return null;
}

// ============================================================================
// EXPORTS
// ============================================================================

export const API_CONFIG = {
  /**
   * The base URL for the API based on configuration
   */
  get baseUrl(): string {
    return getApiBaseUrl();
  },

  /**
   * The API key for authentication
   */
  get apiKey(): string {
    return getApiKey();
  },

  /**
   * Flag indicating if using local API
   */
  isLocal: USE_LOCAL_API,

  /**
   * Flag indicating if running in development mode
   */
  isDev: IS_DEV_MODE,

  /**
   * API endpoints for Classified Order Entry
   */
  endpoints: {
    // GET endpoints
    classifications: '/coe/classifications',
    classificationsTree: '/coe/classificationstree',
    packages: '/coe/packages',
    adFormats: '/coe/adformats',
    features: '/coe/features',
    publications: '/coe/publications',
    customFields: '/coe/customfields',
    serviceInfo: '/coe/serviceinfo',
    orderRunDates: '/coe/orderrundates',
    
    // Association endpoints
    classificationAdFormatAssociations: '/coe/classificationadformatassociations',
    classificationCustomFieldAssociations: '/coe/classificationcustomfieldassociations',
    classificationPackageAssociations: '/coe/classificationpackageassociations',
    packageFeatureAssociations: '/coe/packagefeatureassociations',
    
    // POST endpoints
    priceOrder: '/coe/priceorder',
    placeOrder: '/coe/placeorder',
    placeOrderWithResult: '/coe/placeorderwithresult',
    validateOrder: '/coe/validateorder',
    scheduledAds: '/coe/scheduledads',
    scheduledRetailClassDisplayAds: '/coe/scheduledretailclassdisplayads',
    browseAds: '/coe/browseads',
    browseCounts: '/coe/browsecounts',
    classificationAds: '/coe/classificationads',
    cancelUpcomingRuns: '/coe/cancelupcomingruns',
    
    // Payment endpoints
    paymentConfig: '/coe/paymentconfig',
    processPayment: '/coe/processpayment',
    paidCheckoutPreflight: '/coe/paidcheckout/preflight',
    paidCheckout: '/coe/paidcheckout',
  }
} as const;

export type ApiConfig = typeof API_CONFIG;
